import type { Decoracion, Posicion } from '../../api/ops/sala';
import { GROSOR_PARED, PASO_REJILLA, cajaDePieza, claveDeco, moverPieza, puntosDe, type Caja, type Punto } from './decoracion';

/**
 * Regla de la huella del editor del plano (8.6, G1200; `diseno/comun/docs/componentes.md` «Nada montado ni pegado: la
 * huella» y `diseno/reservas-sala/README.md`), solo en el panel: el servidor no la comprueba. Sin pantalla, para probarla
 * sola.
 *
 * - La huella de una mesa es la mesa con sus sillas: 30 por cada lado (girada si la mesa gira), aunque el panel no dibuje
 *   las sillas. Entre dos huellas de mesa queda un pasillo de 40. Los demás recursos (reservados, hamacas…) no llevan
 *   sillas: 10 de aire.
 * - La decoración también tiene huella: la barra cuenta sus taburetes (34); frente a una mesa, una puerta pide el pasillo
 *   entero (40), una pared 10 y lo demás 20; entre piezas de decoración, 10. Los textos no ocupan sitio. Paredes y puertas
 *   van contra el borde de la sala (pueden salirse) y se tocan entre ellas.
 * - Las mesas de una misma unión van pegadas: entre ellas no hay pasillo, solo no pueden montarse.
 * - La huella entera cabe dentro de la sala.
 */
export const SILLA = 30;
export const PASILLO = 40;
export const AIRE = 10;
const TABURETES = 34;

export interface Escena {
    sala: { ref: string; nombre: string; ancho: number; alto: number };
    /** Las posiciones de la sala. */
    posiciones: readonly Posicion[];
    /** Las piezas de decoración de la sala, con su posición en `decorations`. */
    piezas: readonly { indice: number; pieza: Decoracion }[];
    /** ¿Lleva sillas? Las mesas (`kind = table`); un recurso que no se conoce, también (como el dibujo). */
    conSillas: (resourceId: string) => boolean;
    /** Las uniones predefinidas (sus `resource_ids`). */
    uniones: readonly (readonly string[])[];
    /** Nombre de lo pisado para el aviso: «Mesa P3» o, en la decoración, su tipo en minúscula («barra»). */
    nombre: (clave: string) => string;
}

/** Lo que pisa una pieza: la clave de cada recurso o pieza (`deco:<i>`), y `sala` si se sale. */
export type Choque = string;

interface Huella extends Caja { mesa: boolean }

/** La caja de un rectángulo girado. */
export function cajaGirada(x: number, y: number, w: number, h: number, giro = 0): Caja {
    const r = ((giro % 180) + 180) % 180;
    if (r === 0) {
        return { x, y, width: w, height: h };
    }
    const a = (r * Math.PI) / 180;
    const cw = Math.abs(w * Math.cos(a)) + Math.abs(h * Math.sin(a));
    const ch = Math.abs(w * Math.sin(a)) + Math.abs(h * Math.cos(a));
    return { x: x + w / 2 - cw / 2, y: y + h / 2 - ch / 2, width: cw, height: ch };
}

const crecer = (c: Caja, e: number): Caja => ({ x: c.x - e, y: c.y - e, width: c.width + 2 * e, height: c.height + 2 * e });

export function huellaDeRecurso(p: Posicion, conSillas: boolean): Huella {
    const e = conSillas ? SILLA : 0;
    return { ...crecer(cajaGirada(p.x, p.y, p.width, p.height, p.rotation ?? 0), e), mesa: conSillas };
}

/** Pasillo que pide una pieza frente a una mesa. */
const pasoDe = (d: Decoracion) => (d.kind === 'door' ? PASILLO : d.kind === 'wall' ? AIRE : 20);

export function huellaDePieza(d: Decoracion): Caja {
    return crecer(cajaDePieza(d), d.kind === 'bar' ? TABURETES : 0);
}

export const seTocan = (a: Caja, b: Caja, holgura: number) =>
    a.x < b.x + b.width + holgura && b.x < a.x + a.width + holgura && a.y < b.y + b.height + holgura && b.y < a.y + a.height + holgura;

/** ¿El tramo entra en el rectángulo? (recorte de Liang–Barsky). */
function tramoToca(a: Punto, b: Punto, r: Caja): boolean {
    let t0 = 0;
    let t1 = 1;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    for (const [p, q] of [[-dx, a[0] - r.x], [dx, r.x + r.width - a[0]], [-dy, a[1] - r.y], [dy, r.y + r.height - a[1]]] as const) {
        if (p === 0) {
            if (q < 0) {
                return false;
            }
        } else {
            const t = q / p;
            if (p < 0) {
                if (t > t1) {
                    return false;
                }
                t0 = Math.max(t0, t);
            } else {
                if (t < t0) {
                    return false;
                }
                t1 = Math.min(t1, t);
            }
        }
    }
    return true;
}

function paredToca(puntos: readonly Punto[], r: Caja, holgura: number): boolean {
    const R = crecer(r, holgura + GROSOR_PARED / 2);
    for (let i = 1; i < puntos.length; i += 1) {
        const a = puntos[i - 1];
        const b = puntos[i];
        if (a !== undefined && b !== undefined && tramoToca(a, b, R)) {
            return true;
        }
    }
    return false;
}

const fuera = (c: Caja, sala: Escena['sala']) => c.x < -0.5 || c.y < -0.5 || c.x + c.width > sala.ancho + 0.5 || c.y + c.height > sala.alto + 0.5;

/** Lo que pisa el recurso `p` (ya en su sitio nuevo); `ignorar`, las claves que se mueven con él. */
export function choquesDeRecurso(e: Escena, p: Posicion, ignorar: ReadonlySet<string> = new Set()): Choque[] {
    const a = huellaDeRecurso(p, e.conSillas(p.resource_id));
    const lista: Choque[] = fuera(a, e.sala) ? ['sala'] : [];
    for (const q of e.posiciones) {
        if (q.resource_id === p.resource_id || ignorar.has(q.resource_id)) {
            continue;
        }
        const unidas = e.uniones.some((u) => u.includes(p.resource_id) && u.includes(q.resource_id));
        if (unidas) {
            // Las de una unión van pegadas: solo no pueden montarse los tableros.
            if (seTocan(cajaGirada(p.x, p.y, p.width, p.height, p.rotation ?? 0), cajaGirada(q.x, q.y, q.width, q.height, q.rotation ?? 0), -1)) {
                lista.push(q.resource_id);
            }
            continue;
        }
        const b = huellaDeRecurso(q, e.conSillas(q.resource_id));
        if (seTocan(a, b, a.mesa && b.mesa ? PASILLO : AIRE)) {
            lista.push(q.resource_id);
        }
    }
    for (const { indice, pieza } of e.piezas) {
        if (pieza.kind === 'text' || ignorar.has(claveDeco(indice))) {
            continue;
        }
        const puntos = puntosDe(pieza);
        const toca = puntos === null ? seTocan(a, huellaDePieza(pieza), a.mesa ? pasoDe(pieza) : AIRE) : paredToca(puntos, a, AIRE);
        if (toca) {
            lista.push(claveDeco(indice));
        }
    }
    return lista;
}

/** Lo que pisa la pieza de decoración `indice` con la forma `d`. */
export function choquesDePieza(e: Escena, indice: number, d: Decoracion, ignorar: ReadonlySet<string> = new Set()): Choque[] {
    if (d.kind === 'text') {
        return [];
    }
    const lista: Choque[] = [];
    const puntos = puntosDe(d);
    if (puntos !== null) {
        // Pared por puntos: sus tramos no pisan mesas (con 10 de aire) ni piezas que no sean paredes o puertas.
        for (const q of e.posiciones) {
            if (!ignorar.has(q.resource_id) && paredToca(puntos, huellaDeRecurso(q, e.conSillas(q.resource_id)), AIRE)) {
                lista.push(q.resource_id);
            }
        }
        for (const o of e.piezas) {
            if (o.indice === indice || ignorar.has(claveDeco(o.indice)) || ['text', 'wall', 'door'].includes(o.pieza.kind)) {
                continue;
            }
            if (paredToca(puntos, huellaDePieza(o.pieza), AIRE)) {
                lista.push(claveDeco(o.indice));
            }
        }
        return lista;
    }
    const a = huellaDePieza(d);
    const borde = d.kind === 'wall' || d.kind === 'door';
    if (!borde && fuera(a, e.sala)) {
        lista.push('sala');
    }
    for (const q of e.posiciones) {
        if (ignorar.has(q.resource_id)) {
            continue;
        }
        const b = huellaDeRecurso(q, e.conSillas(q.resource_id));
        if (seTocan(a, b, b.mesa ? pasoDe(d) : AIRE)) {
            lista.push(q.resource_id);
        }
    }
    for (const o of e.piezas) {
        if (o.indice === indice || o.pieza.kind === 'text' || ignorar.has(claveDeco(o.indice))) {
            continue;
        }
        if (borde && (o.pieza.kind === 'wall' || o.pieza.kind === 'door')) {
            continue;
        }
        const pts = puntosDe(o.pieza);
        if (pts === null ? seTocan(a, huellaDePieza(o.pieza), AIRE) : paredToca(pts, a, AIRE)) {
            lista.push(claveDeco(o.indice));
        }
    }
    return lista;
}

/**
 * El hueco libre más cercano (rejilla de 10, anillos crecientes alrededor de donde se quería dejar): la esquina nueva, o
 * `null` si no hay ninguno. `pisa` dice qué pisa la pieza con esa esquina.
 */
export function huecoMasCercano(x: number, y: number, sala: Escena['sala'], pisa: (x: number, y: number) => readonly Choque[]): { x: number; y: number } | null {
    const x0 = Math.round(x / PASO_REJILLA) * PASO_REJILLA;
    const y0 = Math.round(y / PASO_REJILLA) * PASO_REJILLA;
    const limite = Math.ceil(Math.max(sala.ancho, sala.alto) / PASO_REJILLA) + 2;
    for (let r = 0; r <= limite; r += 1) {
        let mejor: { x: number; y: number } | null = null;
        let distancia = Infinity;
        for (let i = -r; i <= r; i += 1) {
            for (let j = -r; j <= r; j += 1) {
                if (Math.max(Math.abs(i), Math.abs(j)) !== r) {
                    continue;
                }
                const d = i * i + j * j;
                const nx = x0 + i * PASO_REJILLA;
                const ny = y0 + j * PASO_REJILLA;
                if (d < distancia && nx >= 0 && ny >= 0 && pisa(nx, ny).length === 0) {
                    mejor = { x: nx, y: ny };
                    distancia = d;
                }
            }
        }
        if (mejor !== null) {
            return mejor;
        }
    }
    return null;
}

/** Hueco para un recurso: la posición con la esquina nueva, o `null`. */
export function huecoParaRecurso(e: Escena, p: Posicion): Posicion | null {
    const h = huecoMasCercano(p.x, p.y, e.sala, (x, y) => choquesDeRecurso(e, { ...p, x, y }));
    return h === null ? null : { ...p, ...h };
}

/** Hueco para una pieza que no es pared: la pieza movida, o `null`. */
export function huecoParaPieza(e: Escena, indice: number, d: Decoracion): Decoracion | null {
    const x = d.geometry.x ?? 0;
    const y = d.geometry.y ?? 0;
    const h = huecoMasCercano(x, y, e.sala, (nx, ny) => choquesDePieza(e, indice, moverPieza(d, nx - x, ny - y)));
    return h === null ? null : moverPieza(d, h.x - x, h.y - y);
}

/** Lo primero que se pisa, para el aviso (el del dibujo): «pisaría Mesa P3», «pisaría barra» o «se saldría de la sala». */
export function queSePisa(e: Escena, choques: readonly Choque[]): string {
    const x = choques[0];
    return x === undefined || x === 'sala' ? 'se saldría de la sala' : `pisaría ${e.nombre(x)}`;
}
